import { useState } from "react";
import { api, type ValetRequestType, type ValetRequestPriority } from "../api";
import { useAuth } from "../AuthContext";

export type Vehicle = {
  id: string;
  plate: string;
  make: string;
  model: string;
  color: string;
};

interface ValetRequestFormProps {
  vehicles: Vehicle[];
  vehiclesLoading: boolean;
  onSubmitSuccess: (requestId: string) => void;
  onCancel: () => void;
}

export type ValetRequestTypeDisplay = {
  id: ValetRequestType;
  title: string;
  description: string;
  icon: string;
  requiresVehicle: boolean;
};

const REQUEST_TYPES: ValetRequestTypeDisplay[] = [
  {
    id: "vehicle_retrieval",
    title: "Vehicle Retrieval",
    description: "Request your vehicle to be brought to the front",
    icon: "",
    requiresVehicle: true,
  },
  {
    id: "early_departure",
    title: "Early Departure",
    description: "Let us know you're leaving early",
    icon: "",
    requiresVehicle: false,
  },
  {
    id: "general_message",
    title: "General Message",
    description: "Send a message to the valet team",
    icon: "",
    requiresVehicle: false,
  },
  {
    id: "expected_visitor",
    title: "Expected Visitor",
    description: "Notify us about an upcoming visitor",
    icon: "",
    requiresVehicle: false,
  },
  {
    id: "ev_charge",
    title: "EV Charging",
    description: "Request charging for your electric vehicle",
    icon: "",
    requiresVehicle: true,
  },
  {
    id: "special_instructions",
    title: "Special Instructions",
    description: "Add specific instructions for your vehicle",
    icon: "",
    requiresVehicle: false,
  },
];

const PRIORITY_OPTIONS: { value: ValetRequestPriority; label: string; color: string }[] = [
  { value: "low", label: "Low Priority", color: "text-gray-600" },
  { value: "normal", label: "Normal", color: "text-blue-600" },
  { value: "urgent", label: "Urgent", color: "text-red-600" },
];

export default function ValetRequestForm({
  vehicles,
  vehiclesLoading,
  onSubmitSuccess,
  onCancel,
}: ValetRequestFormProps) {
  const { token, buildinggId } = useAuth();
  const [requestType, setRequestType] = useState<ValetRequestType>("vehicle_retrieval");
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [message, setMessage] = useState("");
  const [vehicleNotes, setVehicleNotes] = useState("");
  const [priority, setPriority] = useState<ValetRequestPriority>("normal");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedRequestType = REQUEST_TYPES.find((rt) => rt.id === requestType);
  const requiresVehicle = selectedRequestType?.requiresVehicle || false;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!message.trim()) {
      setError("Please enter a message.");
      return;
    }

    if (requiresVehicle && !selectedVehicle) {
      setError("Please select a vehicle.");
      return;
    }

    // Validate required auth context
    if (!token?.residentId || !buildinggId) {
      setError("Session expired. Please log in again.");
      console.error("VALET_REQUEST_MISSING_AUTH", {
        hasToken: !!token,
        hasResidentId: !!token?.residentId,
        hasBuildingId: !!buildinggId,
      });
      return;
    }

    setSubmitting(true);

    try {
      // Cloud Function extracts locationId and residentId from auth token
      // Payload contains only request-specific data
      const response = await api.createValetRequest({
        requestType,
        message,
        vehicleId: selectedVehicle?.id,
        vehicleNotes: requiresVehicle ? vehicleNotes : "",
        priority,
        requestedForAt: new Date().toISOString(),
      });

      if (response.ok && response.requestId) {
        onSubmitSuccess(response.requestId);
      }
    } catch (err: any) {
      setError(err.message || "Failed to create valet request. Please try again.");
      console.error("VALET_REQUEST_SUBMIT_ERROR", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="valet-request-form">
      <h2 className="valet-form-title">New Valet Request</h2>

      {error && (
        <div className="error-message" style={{
          background: "#fee2e2",
          border: "1px solid #fca5a5",
          color: "#991b1b",
          padding: "12px",
          borderRadius: "6px",
          marginBottom: "16px",
          fontSize: "14px",
        }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Request Type Selection */}
        <div className="form-section">
          <label className="form-label">Request Type</label>
          <div className="request-types-grid">
            {REQUEST_TYPES.map((type) => (
              <button
                key={type.id}
                type="button"
                className={`request-type-card ${requestType === type.id ? "selected" : ""}`}
                onClick={() => {
                  setRequestType(type.id);
                  setSelectedVehicle(null);
                  setVehicleNotes("");
                }}
              >
                <div className="request-type-title">{type.title}</div>
                <div className="request-type-description">{type.description}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Vehicle Selection (for vehicle_retrieval) */}
        {requiresVehicle && (
          <div className="form-section">
            <label className="form-label">Select Vehicle *</label>
            {vehiclesLoading ? (
              <div className="loading-text">Loading vehicles...</div>
            ) : vehicles.length === 0 ? (
              <div className="error-message" style={{ color: "#991b1b", fontSize: "14px" }}>
                No vehicles found. Please add a vehicle first.
              </div>
            ) : (
              <div className="vehicles-list">
                {vehicles.map((vehicle) => (
                  <button
                    key={vehicle.id}
                    type="button"
                    className={`vehicle-option ${selectedVehicle?.id === vehicle.id ? "selected" : ""}`}
                    onClick={() => setSelectedVehicle(vehicle)}
                  >
                    <div className="vehicle-license">{vehicle.plate}</div>
                    <div className="vehicle-info">{vehicle.color} {vehicle.make} {vehicle.model}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Vehicle Notes (for vehicle_retrieval) */}
        {requiresVehicle && (
          <div className="form-section">
            <label className="form-label">Vehicle Notes</label>
            <input
              type="text"
              className="form-input"
              placeholder="E.g., Keys are in the glove box, Parked in spot A3..."
              value={vehicleNotes}
              onChange={(e) => setVehicleNotes(e.target.value)}
            />
          </div>
        )}

        {/* Message */}
        <div className="form-section">
          <label className="form-label">Message *</label>
          <textarea
            className="form-textarea"
            placeholder={
              requestType === "vehicle_retrieval"
                ? "E.g., Please bring my car to the front entrance. I'll be there in 5 minutes."
                : requestType === "early_departure"
                  ? "E.g., I'm leaving earlier than expected, please have my car ready by 2pm."
                  : requestType === "expected_visitor"
                    ? "E.g., John Smith will be visiting today, arriving around 3pm. Please grant access."
                    : requestType === "ev_charge"
                      ? "E.g., Please charge my Tesla to 80% by 5pm. It's currently at 20%."
                      : requestType === "special_instructions"
                        ? "E.g., Please don't use the touchless wash, hand wash only. Also check tire pressure."
                        : "Type your message to the valet team..."
            }
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
          />
        </div>

        {/* Priority */}
        <div className="form-section">
          <label className="form-label">Priority</label>
          <div className="priority-options">
            {PRIORITY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`priority-option ${priority === opt.value ? "selected" : ""}`}
                style={priority === opt.value ? { color: opt.value === "urgent" ? "#dc2626" : opt.value === "low" ? "#4b5563" : "#2563eb" } : {}}
                onClick={() => setPriority(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting || (requiresVehicle && !selectedVehicle)}
          >
            {submitting ? "Sending..." : "Send Request"}
          </button>
        </div>
      </form>

      <style>{`
        .valet-request-form {
          background: var(--white);
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          max-width: 600px;
          margin: 0 auto;
        }

        .valet-form-title {
          font-size: 24px;
          font-weight: 600;
          color: var(--stone-900);
          margin: 0 0 24px 0;
          text-align: center;
        }

        .form-section {
          margin-bottom: 24px;
        }

        .form-label {
          display: block;
          font-size: 14px;
          font-weight: 500;
          color: var(--stone-700);
          margin-bottom: 8px;
        }

        .form-input,
        .form-textarea {
          width: 100%;
          padding: 12px;
          border: 1px solid var(--stone-300);
          border-radius: 6px;
          font-size: 14px;
          font-family: inherit;
          box-sizing: border-box;
          transition: border-color 0.2s;
        }

        .form-input:focus,
        .form-textarea:focus {
          outline: none;
          border-color: var(--blue-500);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .form-textarea {
          resize: vertical;
          min-height: 100px;
        }

        .request-types-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }

        .request-type-card {
          padding: 16px;
          border: 2px solid var(--stone-200);
          border-radius: 8px;
          background: var(--white);
          cursor: pointer;
          transition: all 0.2s;
          text-align: left;
        }

        .request-type-card:hover {
          border-color: var(--stone-400);
        }

        .request-type-card.selected {
          border-color: var(--blue-500);
          background: rgba(59, 130, 246, 0.05);
        }

        .request-type-title {
          font-size: 14px;
          font-weight: 600;
          color: var(--stone-900);
          margin-bottom: 4px;
        }

        .request-type-description {
          font-size: 12px;
          color: var(--stone-500);
          line-height: 1.4;
        }

        .vehicles-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .vehicle-option {
          padding: 12px 16px;
          border: 2px solid var(--stone-200);
          border-radius: 8px;
          background: var(--white);
          cursor: pointer;
          transition: all 0.2s;
          text-align: left;
        }

        .vehicle-option:hover {
          border-color: var(--stone-400);
        }

        .vehicle-option.selected {
          border-color: var(--blue-500);
          background: rgba(59, 130, 246, 0.05);
        }

        .vehicle-license {
          font-size: 16px;
          font-weight: 600;
          color: var(--stone-900);
          margin-bottom: 2px;
        }

        .vehicle-info {
          font-size: 13px;
          color: var(--stone-600);
        }

        .priority-options {
          display: flex;
          gap: 8px;
        }

        .priority-option {
          flex: 1;
          padding: 10px 16px;
          border: 2px solid var(--stone-200);
          border-radius: 6px;
          background: var(--white);
          cursor: pointer;
          transition: all 0.2s;
          font-size: 14px;
          font-weight: 500;
          color: var(--stone-600);
        }

        .priority-option:hover {
          border-color: var(--stone-400);
        }

        .priority-option.selected {
          border-color: currentColor;
          background: currentColor;
          color: var(--white) !important;
        }

        .form-actions {
          display: flex;
          gap: 12px;
          margin-top: 32px;
        }

        .btn {
          flex: 1;
          padding: 14px 24px;
          border: none;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .btn-secondary {
          background: var(--stone-100);
          color: var(--stone-700);
        }

        .btn-secondary:hover:not(:disabled) {
          background: var(--stone-200);
        }

        .btn-primary {
          background: var(--blue-500);
          color: var(--white);
        }

        .btn-primary:hover:not(:disabled) {
          background: var(--blue-600);
        }

        .loading-text {
          color: var(--stone-500);
          font-size: 14px;
        }

        @media (max-width: 480px) {
          .request-types-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}
